import React, { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button } from '@mui/material';
import MeetingService from 'services/MeetingService';
import Modal from 'components/shared/Modal';
import { ModalProps } from 'store/types/ComponentProps';
import { Attendee } from 'store/types/Meeting';
import { defaultSnackbarErrorProps } from 'util/layout';

interface RemoveAttendeeModalProps extends ModalProps {
  onSubmit: () => void;
  data?: Attendee;
}

const RemoveAttendeeModal: React.FunctionComponent<RemoveAttendeeModalProps> = ({ onClose, open, onSubmit, data }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (data) {
      setLoading(true);
      MeetingService.removeAttendee(data.meetingId, data.id)
        .then(() => {
          enqueueSnackbar(`Removed ${data.name}`, { variant: 'success' });
          onSubmit();
        })
        .catch((error: string) => {
          enqueueSnackbar(`Failed to remove ${data.name}`, defaultSnackbarErrorProps);
          console.error('Error removing attendee:', error);
        })
        .finally(() => setLoading(false));
    }
  }, [data, enqueueSnackbar, onSubmit]);

  return (
    <Modal
      title={'Remove Attendee'}
      open={open}
      maxWidth={'xs'}
      loading={loading || !data}
      onClose={handleClose}
      onSubmit={handleSubmit}
      actions={
        <>
          <Button color={'secondary'} variant={'outlined'} disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" color={'error'} variant={'contained'}>
            Remove
          </Button>
        </>
      }
    >
      <p>{`Are you sure you want to remove ${data?.name}?`}</p>
    </Modal>
  );
};

export default RemoveAttendeeModal;
